import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { getBranding } from "@/lib/theme";

export default function LoginPage() {
    const navigate = useNavigate();
    const { login } = useAuth();
    // Vỏ trang nhúng sẵn nên có ngay, không phải chờ API (lúc này chưa có token).
    const company = getBranding();
    const [loginId, setLoginId] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [remember, setRemember] = useState(true);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        const errMsg = await login(loginId, password, remember);
        setLoading(false);
        if (!errMsg) {
            navigate("/receiving");
        } else {
            setError(errMsg);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-brand-50 to-white flex flex-col items-center justify-center px-6 py-10">
            {/* Logo & Branding */}
            <div className="flex flex-col items-center mb-8">
                {company.logoUrl ? (
                    <div className="w-24 h-24 rounded-2xl overflow-hidden mb-4 shadow-md bg-white">
                        <img src={company.logoUrl} alt={company.name} className="w-full h-full object-contain" />
                    </div>
                ) : (
                    <div className="w-24 h-24 rounded-2xl mb-4 shadow-md bg-brand-600 flex items-center justify-center">
                        <span className="text-3xl font-bold text-white">
                            {(company.shortName || company.name).slice(0, 3).toUpperCase()}
                        </span>
                    </div>
                )}
                <h1 className="text-2xl font-bold text-brand-800 tracking-tight text-center">{company.name}</h1>
                <p className="text-sm text-gray-500 mt-1 font-medium">Nhận hàng tại kho</p>
            </div>

            {/* Login Card */}
            <div className="w-full max-w-sm bg-white rounded-3xl shadow-lg p-8">
                <h2 className="text-xl font-bold text-gray-800 mb-6">Đăng nhập</h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Username / Email */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Tài khoản</label>
                        <div className="relative">
                            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                                <i className="ri-user-line text-lg"></i>
                            </span>
                            <input
                                type="text"
                                value={loginId}
                                onChange={(e) => setLoginId(e.target.value)}
                                placeholder="mã nhân viên hoặc email"
                                required
                                autoComplete="username"
                                className="w-full pl-10 pr-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100 transition-all"
                            />
                        </div>
                    </div>

                    {/* Password */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Mật khẩu</label>
                        <div className="relative">
                            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                                <i className="ti ti-lock-password text-lg"></i>
                            </span>
                            <input
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                required
                                className="w-full pl-10 pr-12 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100 transition-all"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 cursor-pointer"
                            >
                                <i className={showPassword ? "ri-eye-off-line text-lg" : "ri-eye-line text-lg"}></i>
                            </button>
                        </div>
                    </div>

                    {/* Remember */}
                    <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <div
                                onClick={() => setRemember(!remember)}
                                className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-all cursor-pointer ${remember ? "bg-brand-600 border-brand-600" : "border-gray-300"}`}
                            >
                                {remember && <i className="ri-check-line text-white text-xs"></i>}
                            </div>
                            <span className="text-sm text-gray-600">Ghi nhớ đăng nhập</span>
                        </label>
                        <Link
                            to="/forgot-password"
                            className="text-sm text-orange-500 font-medium cursor-pointer whitespace-nowrap"
                        >
                            Quên mật khẩu?
                        </Link>
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                            <i className="ri-error-warning-line text-red-500"></i>
                            <p className="text-sm text-red-600">{error}</p>
                        </div>
                    )}

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-brand-600 to-brand-500 text-white font-bold py-3.5 rounded-xl text-base shadow-md active:scale-95 transition-all cursor-pointer whitespace-nowrap disabled:opacity-70 mt-2"
                    >
                        {loading ? (
                            <span className="flex items-center justify-center gap-2">
                                <i className="ri-loader-4-line animate-spin"></i> Đang đăng nhập...
                            </span>
                        ) : (
                            <span className="flex items-center justify-center gap-2">
                                <i className="ti ti-login-2"></i>
                                Đăng Nhập
                            </span>
                        )}
                    </button>
                </form>
            </div>

            {/* Footer */}
            <div className="mt-8 text-center">
                <p className="text-xs text-gray-400">© {new Date().getFullYear()} {company.name}</p>
            </div>
        </div>
    );
}
